import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, AlertTriangle, FileText, FileSearch, BarChart3 } from 'lucide-react';
import { incidentService } from '../../services/incidentService';
import { incidentRootCauseService } from '../../services/incidentRootCauseService';
import { riskService } from '../../services/riskService';
import { riskControlService } from '../../services/riskControlService';
import { departmentService } from '../../services/firestoreService';
import { protocolService } from '../../services/protocolService';
import processService from '../../services/processService';
import IncidentAnalyticsDashboard from './IncidentAnalyticsDashboard';
import RCAForm from '../rca/RCAForm';
import './IncidentList.css';

const INCIDENT_TYPES = [
  { value: 'HAI', labelKey: 'typeHAI' },
  { value: 'Near miss', labelKey: 'typeNearMiss' },
  { value: 'Device failure', labelKey: 'typeDeviceFailure' },
  { value: 'Other', labelKey: 'typeOther' }
];

const SEVERITIES = [
  { value: 'Critical', labelKey: 'severityCritical' },
  { value: 'High', labelKey: 'severityHigh' },
  { value: 'Medium', labelKey: 'severityMedium' },
  { value: 'Low', labelKey: 'severityLow' }
];

const ROOT_CAUSE_STATUSES = [
  { value: 'Planned', labelKey: 'statusPlanned' },
  { value: 'In Progress', labelKey: 'statusInProgress' },
  { value: 'Completed', labelKey: 'statusCompleted' }
];

const IncidentList = ({ organizationId, userId, userRole, initialView = 'list' }) => {
  const { t } = useTranslation('incidents');
  const [showAnalytics, setShowAnalytics] = useState(initialView === 'analytics');
  const [incidents, setIncidents] = useState([]);
  const [risks, setRisks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [originFilter, setOriginFilter] = useState(''); // '' | 'manual' | 'system_generated'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [detailIncident, setDetailIncident] = useState(null);
  const [rootCauses, setRootCauses] = useState([]);
  const [controlsForRisk, setControlsForRisk] = useState([]);
  const [showRootCauseForm, setShowRootCauseForm] = useState(false);
  const [editingRootCauseId, setEditingRootCauseId] = useState(null);
  const [deleteRootCauseConfirm, setDeleteRootCauseConfirm] = useState(null);
  const [rcaFormRcaId, setRcaFormRcaId] = useState(null);
  const [rcaFormIncidentId, setRcaFormIncidentId] = useState(null);
  const [rootCauseForm, setRootCauseForm] = useState({
    failedControlId: '',
    rootCauseText: '',
    contributingFactors: '',
    correctiveActions: '',
    preventiveActions: '',
    status: 'Planned'
  });
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    type: 'HAI',
    severity: 'Medium',
    description: '',
    patientRef: '',
    riskId: '',
    protocolId: '',
    standardId: '',
    departmentId: '',
    deviationId: ''
  });

  const fetchIncidents = async () => {
    setLoading(true);
    setError(null);
    if (!organizationId) {
      setIncidents([]);
      setLoading(false);
      return;
    }
    try {
      const data = await incidentService.getIncidentsByOrganization(organizationId);
      setIncidents(data);
    } catch (err) {
      console.error('Fetch incidents:', err);
      setError(err);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRisks = async () => {
    try {
      const data = await riskService.getAllRisks();
      setRisks(data || []);
    } catch (_) {}
  };

  const fetchDepartments = async () => {
    try {
      const data = await departmentService.getAll();
      setDepartments(data || []);
    } catch (_) {}
  };

  const fetchProtocols = async () => {
    try {
      const data = await protocolService.getAllProtocols({});
      setProtocols(data || []);
    } catch (_) {}
  };

  const fetchProcesses = async () => {
    if (!organizationId) return;
    try {
      const data = await processService.getProcesses(organizationId);
      setProcesses(data || []);
    } catch (_) {}
  };

  useEffect(() => {
    fetchIncidents();
    fetchRisks();
    fetchDepartments();
    fetchProtocols();
    fetchProcesses();
  }, [organizationId]);

  const filteredIncidents = React.useMemo(() => {
    if (!originFilter) return incidents;
    return incidents.filter(inc => (inc.origin || 'manual') === originFilter);
  }, [incidents, originFilter]);

  useEffect(() => {
    if (!detailIncident) {
      setRootCauses([]);
      setControlsForRisk([]);
      return;
    }
    (async () => {
      const list = await incidentRootCauseService.getByIncidentId(detailIncident.id);
      setRootCauses(list);
      if (detailIncident.riskId) {
        const ctrl = await riskControlService.getControlsByRiskId(detailIncident.riskId);
        setControlsForRisk(ctrl);
      } else {
        setControlsForRisk([]);
      }
    })();
  }, [detailIncident]);

  const canCreateRCA = ['management', 'ic_personnel'].includes(userRole);

  const openRCAForm = (rcaId, incidentId) => {
    setRcaFormRcaId(rcaId);
    setRcaFormIncidentId(incidentId);
    setDetailIncident(null);
  };

  const closeRCAForm = () => {
    setRcaFormRcaId(null);
    setRcaFormIncidentId(null);
    fetchIncidents();
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      date: new Date().toISOString().slice(0, 10),
      type: 'HAI',
      severity: 'Medium',
      description: '',
      patientRef: '',
      riskId: '',
      protocolId: '',
      standardId: '',
      departmentId: '',
      deviationId: ''
    });
    setShowForm(true);
  };

  const openEdit = (inc) => {
    setEditingId(inc.id);
    const d = inc.date?.toDate?.();
    setForm({
      date: d ? d.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      type: inc.type || 'HAI',
      severity: inc.severity || 'Medium',
      description: inc.description || '',
      patientRef: inc.patientRef || '',
      riskId: inc.riskId || '',
      protocolId: inc.protocolId || '',
      standardId: inc.standardId || '',
      departmentId: inc.departmentId || '',
      deviationId: inc.deviationId || ''
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    const payload = {
      organizationId,
      date: form.date,
      type: form.type,
      severity: form.severity,
      description: form.description.trim(),
      patientRef: form.patientRef.trim() || null,
      riskId: form.riskId || null,
      protocolId: form.protocolId || null,
      standardId: form.standardId || null,
      departmentId: form.departmentId || null,
      deviationId: form.deviationId?.trim() || null,
      reportedBy: userId
    };
    try {
      if (editingId) {
        await incidentService.updateIncident(editingId, payload, userId || 'current-user');
      } else {
        await incidentService.createIncident(payload, userId || 'current-user');
      }
      setShowForm(false);
      fetchIncidents();
    } catch (err) {
      console.error('Save incident:', err);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await incidentService.deleteIncident(deleteConfirm.id);
      setDeleteConfirm(null);
      setDetailIncident(null);
      fetchIncidents();
    } catch (err) {
      console.error('Delete incident:', err);
    }
  };

  const openRootCauseForm = (rc) => {
    if (rc) {
      setEditingRootCauseId(rc.id);
      setRootCauseForm({
        failedControlId: rc.failedControlId || '',
        rootCauseText: rc.rootCauseText || '',
        contributingFactors: rc.contributingFactors || '',
        correctiveActions: rc.correctiveActions || '',
        preventiveActions: rc.preventiveActions || '',
        status: rc.status || 'Planned'
      });
    } else {
      setEditingRootCauseId(null);
      setRootCauseForm({
        failedControlId: '',
        rootCauseText: '',
        contributingFactors: '',
        correctiveActions: '',
        preventiveActions: '',
        status: 'Planned'
      });
    }
    setShowRootCauseForm(true);
  };

  const saveRootCause = async () => {
    if (!detailIncident) return;
    const uid = userId || 'current-user';
    try {
      if (editingRootCauseId) {
        await incidentRootCauseService.update(editingRootCauseId, rootCauseForm, uid);
      } else {
        await incidentRootCauseService.create({
          incidentReportId: detailIncident.id,
          ...rootCauseForm,
          failedControlId: rootCauseForm.failedControlId || null
        }, uid);
      }
      setShowRootCauseForm(false);
      const list = await incidentRootCauseService.getByIncidentId(detailIncident.id);
      setRootCauses(list);
    } catch (err) {
      console.error('Save root cause:', err);
    }
  };

  const confirmDeleteRootCause = async () => {
    if (!deleteRootCauseConfirm) return;
    try {
      await incidentRootCauseService.delete(deleteRootCauseConfirm.id);
      setDeleteRootCauseConfirm(null);
      const list = await incidentRootCauseService.getByIncidentId(detailIncident?.id);
      setRootCauses(list || []);
    } catch (err) {
      console.error('Delete root cause:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    if (timestamp.toDate) return timestamp.toDate().toLocaleDateString();
    if (timestamp.seconds) return new Date(timestamp.seconds * 1000).toLocaleDateString();
    return String(timestamp);
  };

  if (loading) {
    return (
      <div className="incident-list-loading">
        <div className="loading-spinner" />
        <p>{t('loadingIncidents')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="incident-list-error">
        <AlertTriangle className="error-icon" />
        <p>{error.message}</p>
        <button onClick={fetchIncidents} className="btn btn-secondary">{t('cancel')}</button>
      </div>
    );
  }

  if (showAnalytics) {
    return (
      <IncidentAnalyticsDashboard
        incidents={incidents}
        risks={risks}
        protocols={protocols}
        onBack={() => setShowAnalytics(false)}
      />
    );
  }

  return (
    <div className="incident-list-container">
      <div className="incident-list-header">
        <div>
          <h1>{t('incidentReports')}</h1>
          <p className="subtitle">{t('incidentReportsSubtitle')}</p>
        </div>
        <div className="incident-list-actions">
          <select
            value={originFilter}
            onChange={e => setOriginFilter(e.target.value)}
            className="origin-filter-select"
            title={t('filterByOrigin')}
          >
            <option value="">{t('filterAll')}</option>
            <option value="manual">{t('originManual')}</option>
            <option value="system_generated">{t('originSystem')}</option>
          </select>
          <button type="button" className="btn btn-secondary" onClick={() => setShowAnalytics(true)}>
            <BarChart3 size={16} />
            {t('analytics')}
          </button>
          <button onClick={openCreate} className="btn btn-primary">
            <Plus size={16} />
            {t('createIncident')}
          </button>
        </div>
      </div>

      {filteredIncidents.length === 0 ? (
        <div className="empty-state">
          <FileText className="empty-icon" />
          <h3>{incidents.length === 0 ? t('noIncidentsYet') : t('noIncidentsMatchFilter')}</h3>
          {incidents.length === 0 ? (
            <button onClick={openCreate} className="btn btn-primary">
              <Plus size={16} />
              {t('createIncident')}
            </button>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={() => setOriginFilter('')}>{t('clearFilter')}</button>
          )}
        </div>
      ) : (
        <div className="incident-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('date')}</th>
                <th>{t('origin')}</th>
                <th>{t('type')}</th>
                <th>{t('severity')}</th>
                <th>{t('description')}</th>
                <th>{t('linkedRisk')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredIncidents.map(inc => (
                <tr key={inc.id}>
                  <td>{formatDate(inc.date)}</td>
                  <td>
                    <span className={`origin-badge origin-${(inc.origin || 'manual')}`}>
                      {inc.origin === 'system_generated' ? t('originSystem') : t('originManual')}
                    </span>
                  </td>
                  <td>{inc.type || '—'}</td>
                  <td><span className={`severity-badge severity-${(inc.severity || '').toLowerCase()}`}>{inc.severity || '—'}</span></td>
                  <td><div className="description-cell" title={inc.description}>{(inc.description || '').slice(0, 80)}{(inc.description || '').length > 80 ? '…' : ''}</div></td>
                  <td>{inc.riskId ? (risks.find(r => r.id === inc.riskId)?.riskScenario || inc.riskId) : '—'}</td>
                  <td>
                    <button type="button" className="btn btn-icon btn-view" title={t('viewIncident')} onClick={() => setDetailIncident(inc)}><FileSearch size={14} /></button>
                    <button type="button" className="btn btn-edit btn-icon" title={t('editIncident')} onClick={() => openEdit(inc)}><Edit size={14} /></button>
                    <button type="button" className="btn btn-delete btn-icon" title={t('deleteIncident')} onClick={() => setDeleteConfirm(inc)}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content incident-form" onClick={e => e.stopPropagation()}>
            <h3>{editingId ? t('editIncidentTitle') : t('addIncident')}</h3>
            <div className="form-group">
              <label>{t('date')}</label>
              <input type="date" value={form.date} onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t('incidentType')}</label>
              <select value={form.type} onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}>
                {INCIDENT_TYPES.map(opt => <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('incidentSeverity')}</label>
              <select value={form.severity} onChange={e => setForm(prev => ({ ...prev, severity: e.target.value }))}>
                {SEVERITIES.map(opt => <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('incidentDescription')}</label>
              <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} rows={4} />
            </div>
            <div className="form-group">
              <label>{t('optionalPatientRef')}</label>
              <input type="text" value={form.patientRef} onChange={e => setForm(prev => ({ ...prev, patientRef: e.target.value }))} placeholder="—" />
            </div>
            <div className="form-group">
              <label>{t('linkToRisk')}</label>
              <select value={form.riskId} onChange={e => setForm(prev => ({ ...prev, riskId: e.target.value }))}>
                <option value="">—</option>
                {risks.map(r => <option key={r.id} value={r.id}>{r.riskScenario}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('linkToProtocol')}</label>
              <select value={form.protocolId} onChange={e => setForm(prev => ({ ...prev, protocolId: e.target.value }))}>
                <option value="">—</option>
                {protocols.map(p => <option key={p.id || p.firestoreId} value={p.firestoreId || p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('linkToStandard')}</label>
              <select value={form.standardId} onChange={e => setForm(prev => ({ ...prev, standardId: e.target.value }))}>
                <option value="">—</option>
                {processes.map(p => <option key={p.id} value={p.id}>{p.processName || p.processCode}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('department')}</label>
              <select value={form.departmentId} onChange={e => setForm(prev => ({ ...prev, departmentId: e.target.value }))}>
                <option value="">—</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name || d.departmentCode || d.id}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('linkedDeviation')} ({t('optional')})</label>
              <input
                type="text"
                value={form.deviationId}
                onChange={e => setForm(prev => ({ ...prev, deviationId: e.target.value }))}
                placeholder={t('deviationIdPlaceholder')}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={handleSave}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h3>{t('confirmDeleteIncidentTitle')}</h3>
            <p>{t('confirmDeleteIncident')}</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>{t('cancel')}</button>
              <button type="button" className="btn btn-danger" onClick={confirmDelete}>{t('delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Incident detail + Root cause analysis */}
      {detailIncident && (
        <div className="modal-overlay" onClick={() => setDetailIncident(null)}>
          <div className="modal-content incident-detail" onClick={e => e.stopPropagation()}>
            <h3>{formatDate(detailIncident.date)} — {detailIncident.type} ({detailIncident.severity})</h3>
            <p className="detail-description">{detailIncident.description}</p>
            {detailIncident.riskId && <p><strong>{t('linkedRisk')}:</strong> {risks.find(r => r.id === detailIncident.riskId)?.riskScenario || detailIncident.riskId}</p>}
            {detailIncident.protocolId && <p><strong>{t('linkedProtocol')}:</strong> {protocols.find(p => (p.firestoreId || p.id) === detailIncident.protocolId)?.name || detailIncident.protocolId}</p>}
            {detailIncident.deviationId && (
              <p><strong>{t('linkedDeviation')}:</strong> {detailIncident.deviationId}</p>
            )}
            {canCreateRCA && (
              <div className="incident-rca-actions">
                {detailIncident.rcaId && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => openRCAForm(detailIncident.rcaId, detailIncident.id)}
                  >
                    {t('viewRCA')}
                  </button>
                )}
                {!detailIncident.rcaId && detailIncident.deviationId && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => openRCAForm(null, detailIncident.id)}
                  >
                    {t('createRCA')}
                  </button>
                )}
                {!detailIncident.rcaId && !detailIncident.deviationId && (
                  <p className="form-hint">{t('rcaNeedsDeviation')}</p>
                )}
              </div>
            )}
            <hr />
            <h4>{t('rootCauseAnalysis')}</h4>
            <div className="root-cause-toolbar">
              <button type="button" className="btn btn-primary btn-sm" onClick={() => openRootCauseForm(null)}><Plus size={14} /> {t('addRootCause')}</button>
            </div>
            {rootCauses.length === 0 ? (
              <p className="form-hint">{t('noRootCausesYet')}</p>
            ) : (
              <ul className="root-cause-list">
                {rootCauses.map(rc => (
                  <li key={rc.id}>
                    <div className="root-cause-item">
                      <div><strong>{rc.rootCauseText || '—'}</strong></div>
                      {rc.failedControlId && <div className="root-cause-meta">{t('failedControl')}: {controlsForRisk.find(c => c.id === rc.failedControlId)?.controlType || rc.failedControlId}</div>}
                      <div className="root-cause-meta">{t('status')}: {rc.status}</div>
                      <div className="root-cause-actions">
                        <button type="button" className="btn btn-edit btn-icon" onClick={() => openRootCauseForm(rc)}><Edit size={12} /></button>
                        <button type="button" className="btn btn-delete btn-icon" onClick={() => setDeleteRootCauseConfirm(rc)}><Trash2 size={12} /></button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={() => setDetailIncident(null)}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Root cause Add/Edit form */}
      {showRootCauseForm && detailIncident && (
        <div className="modal-overlay" onClick={() => setShowRootCauseForm(false)}>
          <div className="modal-content incident-form" onClick={e => e.stopPropagation()}>
            <h3>{editingRootCauseId ? t('editRootCause') : t('addRootCause')}</h3>
            <div className="form-group">
              <label>{t('failedControl')}</label>
              {detailIncident.protocolId && detailIncident.origin === 'system_generated' && (
                <p className="form-hint" style={{ marginBottom: 6 }}>
                  {t('protocolExecuted')}: {protocols.find(p => (p.firestoreId || p.id) === detailIncident.protocolId)?.name || detailIncident.protocolId}
                </p>
              )}
              <select value={rootCauseForm.failedControlId} onChange={e => setRootCauseForm(prev => ({ ...prev, failedControlId: e.target.value }))}>
                <option value="">—</option>
                {controlsForRisk.map(c => {
                  const dispLabel = (c.referenceType === 'protocol' && detailIncident.protocolId && (c.referenceId === detailIncident.protocolId))
                    ? (protocols.find(p => (p.firestoreId || p.id) === detailIncident.protocolId)?.name || c.referenceLabel)
                    : c.referenceLabel;
                  return <option key={c.id} value={c.id}>{c.controlType} {dispLabel ? `– ${dispLabel}` : ''}</option>;
                })}
              </select>
            </div>
            <div className="form-group">
              <label>{t('rootCauseText')}</label>
              <textarea value={rootCauseForm.rootCauseText} onChange={e => setRootCauseForm(prev => ({ ...prev, rootCauseText: e.target.value }))} rows={3} />
            </div>
            <div className="form-group">
              <label>{t('contributingFactors')}</label>
              <textarea value={rootCauseForm.contributingFactors} onChange={e => setRootCauseForm(prev => ({ ...prev, contributingFactors: e.target.value }))} rows={2} />
            </div>
            <div className="form-group">
              <label>{t('correctiveActions')}</label>
              <textarea value={rootCauseForm.correctiveActions} onChange={e => setRootCauseForm(prev => ({ ...prev, correctiveActions: e.target.value }))} rows={2} />
            </div>
            <div className="form-group">
              <label>{t('preventiveActions')}</label>
              <textarea value={rootCauseForm.preventiveActions} onChange={e => setRootCauseForm(prev => ({ ...prev, preventiveActions: e.target.value }))} rows={2} />
            </div>
            <div className="form-group">
              <label>{t('status')}</label>
              <select value={rootCauseForm.status} onChange={e => setRootCauseForm(prev => ({ ...prev, status: e.target.value }))}>
                {ROOT_CAUSE_STATUSES.map(opt => <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>)}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowRootCauseForm(false)}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={saveRootCause}>{t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {deleteRootCauseConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteRootCauseConfirm(null)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <h3>{t('confirmDeleteRootCauseTitle')}</h3>
            <p>{t('confirmDeleteRootCause')}</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteRootCauseConfirm(null)}>{t('cancel')}</button>
              <button type="button" className="btn btn-danger" onClick={confirmDeleteRootCause}>{t('delete')}</button>
            </div>
          </div>
        </div>
      )}

      {(rcaFormRcaId || rcaFormIncidentId) && organizationId && (
        <RCAForm
          organizationId={organizationId}
          userId={userId}
          userRole={userRole}
          rcaId={rcaFormRcaId || undefined}
          incidentId={!rcaFormRcaId ? rcaFormIncidentId : null}
          onSave={closeRCAForm}
          onCancel={closeRCAForm}
        />
      )}
    </div>
  );
};

export default IncidentList;
